import { parseDate, today, type CalendarDate } from '@internationalized/date';
import formatCalendarDate from '@orn/shared-web/utils/formatCalendarDate';
import formatDateTime from '@orn/shared-web/utils/formatDateTime';

// `formatCalendarDate`, `formatDateTime`, and `toCurrency` were promoted to
// `@orn/shared-web` so `@orn/mops-web` can consume them without copy-paste
// drift. Re-exported here so existing call sites in this package keep working
// unchanged.
export { formatCalendarDate, formatDateTime };

/** Formats as "Jan 20, 2026 @ 2:26 PM" */
export function formatDateAtTime(dateString: string): string {
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  const timePart = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  return `${datePart} at ${timePart}`;
}

/**
 * Capitalizes the first letter of a string and lowercases the rest
 * @example firstLetterCapitalize('HELLO') // Returns "Hello"
 */
export function firstLetterCapitalize(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export type PeriodType = 'day' | 'month' | 'year';

/**
 * Formats a YYYY-MM-DD date string for chart labels in short format.
 *
 * When `referenceYear` is provided, day and month labels from a different
 * year get a two-digit year tag so multi-year axes stay unambiguous.
 * @example formatChartPeriodShort('2024-03-15', 'day') // Returns "Mar 15"
 * @example formatChartPeriodShort('2024-03-15', 'month') // Returns "Mar"
 * @example formatChartPeriodShort('2024-03-15', 'year') // Returns "2024"
 * @example formatChartPeriodShort('2024-03-15', 'month', 2025) // Returns "Mar '24"
 */
export function formatChartPeriodShort(dateString: string, periodType: PeriodType, referenceYear?: number): string {
  const calendarDate = parseDate(dateString);
  const date = new Date(Date.UTC(calendarDate.year, calendarDate.month - 1, calendarDate.day));

  const yearTag =
    referenceYear !== undefined && calendarDate.year !== referenceYear
      ? ` '${String(calendarDate.year % 100).padStart(2, '0')}`
      : '';

  if (periodType === 'day') {
    const label = date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
    return `${label}${yearTag}`;
  } else if (periodType === 'year') {
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      timeZone: 'UTC',
    });
  }
  // month
  const label = date.toLocaleDateString(undefined, {
    month: 'short',
    timeZone: 'UTC',
  });
  return `${label}${yearTag}`;
}

/**
 * Formats a YYYY-MM-DD date string for chart labels in long format
 * @example formatChartPeriodLong('2024-03-15', 'day') // Returns "March 15, 2024"
 * @example formatChartPeriodLong('2024-03-15', 'month') // Returns "March 2024"
 * @example formatChartPeriodLong('2024-03-15', 'year') // Returns "2024"
 */
export function formatChartPeriodLong(dateString: string, periodType: PeriodType): string {
  const calendarDate = parseDate(dateString);

  if (periodType === 'day') {
    return formatCalendarDate(calendarDate, 'long');
  } else if (periodType === 'year') {
    return calendarDate.year.toString();
  }
  // month
  const date = new Date(Date.UTC(calendarDate.year, calendarDate.month - 1, calendarDate.day));
  return date.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

export { default as toCurrency } from '@orn/shared-web/utils/toCurrency';

/**
 * Formats a divided value with adaptive decimal places:
 *   < 10  → up to 2 decimals  (e.g. 1.51, 9.9)
 *   < 100 → up to 1 decimal   (e.g. 15.5, 99)
 *   ≥ 100 → whole number       (e.g. 150)
 * Trailing zeros are stripped so 1.50 → "1.5", 2.00 → "2".
 */
function formatCompactNumber(value: number): string {
  if (value < 10) {
    return value.toFixed(2).replace(/\.?0+$/, '');
  }
  if (value < 100) {
    return value.toFixed(1).replace(/\.?0+$/, '');
  }
  return value.toFixed(0);
}

/**
 * Formats money values with short abbreviations (K, M, B, T).
 *
 * `precision` controls how many decimals appear in the abbreviated form:
 *   - `'adaptive'` (default) — up to 2 decimals for small quotients, fewer for larger ones.
 *   - `'whole'` — always rounds to the nearest whole unit (e.g. $2K, $3M). Best for chart axes.
 *
 * @example formatMoneyShort(1509)                            // "$1.51K"  (adaptive)
 * @example formatMoneyShort(1509, { precision: 'whole' })    // "$2K"     (whole)
 * @example formatMoneyShort(15500)                           // "$15.5K"  (adaptive)
 * @example formatMoneyShort(15500, { precision: 'whole' })   // "$16K"    (whole)
 * @example formatMoneyShort(-5000)                           // "-$5K"
 * @example formatMoneyShort(42.5, { showCents: true })       // "$42.50"
 * @example formatMoneyShort(2500000)                         // "$2.5M"   (adaptive)
 */
export function formatMoneyShort(
  value: number | string,
  args?: { showCents?: boolean; precision?: 'adaptive' | 'whole' }
): string {
  const { showCents = false, precision = 'adaptive' } = args ?? {};
  const num = typeof value === 'string' ? parseFloat(value) : value;
  const absValue = Math.abs(num);
  const sign = num < 0 ? '-' : '';
  const fmt = precision === 'whole' ? (v: number) => v.toFixed(0) : formatCompactNumber;

  if (absValue >= 1_000_000_000_000) {
    return `${sign}$${fmt(absValue / 1_000_000_000_000)}T`;
  } else if (absValue >= 1_000_000_000) {
    return `${sign}$${fmt(absValue / 1_000_000_000)}B`;
  } else if (absValue >= 1_000_000) {
    return `${sign}$${fmt(absValue / 1_000_000)}M`;
  } else if (absValue >= 1000) {
    return `${sign}$${fmt(absValue / 1000)}K`;
  }
  if (showCents) {
    const formatted = absValue.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return `${sign}$${formatted}`;
  }
  return `${sign}$${absValue}`;
}

/**
 * Formats a number as currency with thousands separators and 0 decimal places
 * @example formatCurrencyWhole(1234) // Returns "$1,234"
 * @example formatCurrencyWhole(1234.56) // Returns "$1,235" (rounded)
 * @example formatCurrencyWhole(-1234) // Returns "-$1,234"
 */
export function formatCurrencyWhole(value: number): string {
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  const formatted = absValue.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return `${sign}$${formatted}`;
}

export type RelativeDateBucket =
  | { direction: 'past' | 'future'; value: number; unit: 'day' | 'week' | 'month' }
  | { direction: 'today'; value: 0; unit: 'day' };

/**
 * Computes the relative distance from today to the given date, bucketed into
 * a human-friendly unit:
 *   - < 14 days  → unit: 'day'   (value = raw day count)
 *   - 14–59 days → unit: 'week'  (value = floor(days / 7))
 *   - 60+ days   → unit: 'month' (value = floor(days / 30))
 *
 * `direction` indicates whether the date is in the 'past', 'future', or 'today'.
 */
export function getRelativeDateBucket(date: CalendarDate): RelativeDateBucket {
  const targetDate = new Date(date.toString() + 'T00:00:00');
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  const diffDays = Math.round((targetDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return { direction: 'today', value: 0, unit: 'day' };
  }

  const direction = diffDays > 0 ? 'future' : 'past';
  const absDays = Math.abs(diffDays);

  if (absDays >= 60) {
    return { direction, value: Math.floor(absDays / 30), unit: 'month' };
  }
  if (absDays >= 14) {
    return { direction, value: Math.floor(absDays / 7), unit: 'week' };
  }
  return { direction, value: absDays, unit: 'day' };
}

/**
 * Simple English pluralization: appends "s" when value !== 1.
 * @example pluralize(1, 'day')   // "1 day"
 * @example pluralize(3, 'week')  // "3 weeks"
 */
export function pluralize(value: number, unit: string): string {
  return `${value} ${unit}${value === 1 ? '' : 's'}`;
}

/**
 * Formats a date string to show days until/since that date.
 * Rolls up to months when the difference is >= 60 days (using 30 days per month).
 * @example formatDaysUntil('2025-01-07') // Returns "In 2 days" (if today is Jan 5)
 * @example formatDaysUntil('2025-01-05') // Returns "Today"
 * @example formatDaysUntil('2025-01-03') // Returns "2 days ago" (if today is Jan 5)
 * @example formatDaysUntil('2024-11-05') // Returns "2 months ago" (if today is Jan 5)
 */
export function formatDaysUntil(date: CalendarDate): string {
  const bucket = getRelativeDateBucket(date);

  if (bucket.direction === 'today') {
    return 'Today';
  }
  if (bucket.direction === 'future' && bucket.value === 1 && bucket.unit === 'day') {
    return 'Tomorrow';
  }
  if (bucket.direction === 'past' && bucket.value === 1 && bucket.unit === 'day') {
    return 'Yesterday';
  }

  const label = pluralize(bucket.value, bucket.unit);
  return bucket.direction === 'future' ? `In ${label}` : `${label} ago`;
}

/**
 * Like formatDaysUntil, but flags past dates as missed expectations.
 * @example formatExpectedDaysUntil('2025-01-01') // Returns "Expected 4 days ago" (if today is Jan 5)
 * @example formatExpectedDaysUntil('2025-01-04') // Returns "Expected yesterday" (if today is Jan 5)
 * @example formatExpectedDaysUntil('2025-01-07') // Returns "In 2 days" (if today is Jan 5)
 */
export function formatExpectedDaysUntil(date: CalendarDate): string {
  const bucket = getRelativeDateBucket(date);

  if (bucket.direction !== 'past') {
    return formatDaysUntil(date);
  }
  if (bucket.value === 1 && bucket.unit === 'day') {
    return 'Expected yesterday';
  }
  return `Expected ${pluralize(bucket.value, bucket.unit)} ago`;
}

/**
 * Formats a number with its ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
 * @example formatOrdinal(1) // Returns "1st"
 * @example formatOrdinal(2) // Returns "2nd"
 * @example formatOrdinal(3) // Returns "3rd"
 * @example formatOrdinal(4) // Returns "4th"
 * @example formatOrdinal(11) // Returns "11th"
 * @example formatOrdinal(21) // Returns "21st"
 * @example formatOrdinal(22) // Returns "22nd"
 * @example formatOrdinal(23) // Returns "23rd"
 */
export function formatOrdinal(value: number): string {
  const absValue = Math.abs(value);
  const lastTwoDigits = absValue % 100;
  const lastDigit = absValue % 10;

  // Special case: 11, 12, 13 always use "th"
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return `${value}th`;
  }

  // Standard rules based on last digit
  const suffix = lastDigit === 1 ? 'st' : lastDigit === 2 ? 'nd' : lastDigit === 3 ? 'rd' : 'th';
  return `${value}${suffix}`;
}

/**
 * Formats the "since" text based on the date range start
 * Returns special text for start of month/year, otherwise formatted date
 */
export function formatSinceText(startDate: CalendarDate): string {
  const now = today('UTC');
  const isFirstOfMonth = startDate.day === 1;
  const isFirstOfYear = isFirstOfMonth && startDate.month === 1;
  const isCurrentMonth = startDate.month === now.month && startDate.year === now.year;
  const isCurrentYear = startDate.year === now.year;

  if (isFirstOfYear && isCurrentYear) {
    return 'since start of year';
  }

  if (isFirstOfMonth && isCurrentMonth) {
    return 'since start of month';
  }

  const formattedDate = formatCalendarDate(startDate, 'long');
  return `since ${formattedDate}`;
}

/**
 * Formats a date string to show how many weeks ago it was.
 * Accepts both date-only strings (e.g. '2025-01-15') and full ISO datetimes (e.g. '2025-01-15T10:30:00.000Z').
 * @example formatWeeksAgo('2025-01-15') // Returns "This week" (if today is Jan 22)
 * @example formatWeeksAgo('2025-01-08') // Returns "Last week" (if today is Jan 22)
 * @example formatWeeksAgo('2025-01-01') // Returns "3 weeks ago" (if today is Jan 22)
 */
export function formatWeeksAgo(dateString: string): string {
  // Extract YYYY-MM-DD for consistent parsing (handles both date-only and full DateTimeISO)
  const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  const targetDate = new Date(datePart + 'T12:00:00Z');
  if (Number.isNaN(targetDate.getTime())) {
    return 'This week';
  }
  targetDate.setUTCHours(0, 0, 0, 0);
  const now = new Date();
  const todayDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  const diffMs = todayDate.getTime() - targetDate.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffWeeks <= 0) {
    return 'This week';
  } else if (diffWeeks === 1) {
    return 'Last week';
  } else {
    return `${diffWeeks} weeks ago`;
  }
}
